"use client";

import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import useOrder from "../hooks/useOrder";
import { useCloud } from "../hooks/useCloud";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faPrint } from "@fortawesome/free-solid-svg-icons";
import {
  Filter,
  X,
  Search,
  RefreshCcwDot,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Reprint } from "../Component/Print/ReprintBill";
import { SUCCESS } from "../assets/constants";
import store from "../store";
import toast from "react-hot-toast";

const getOrderStatus = (status) => {
  switch (status) {
    case "settle_later":
      return "Pending";
    case "pending":
      return "Live";
    case "paid":
      return "Paid";
    default:
      return status;
  }
};

const getOredrStatusColor = (status) => {
  switch (status) {
    case "settle_later":
      return "bg-yellow-100 text-yellow-800 cursor-pointer";
    case "pending":
      return "bg-[#FFA500]  text-white";
    case "cancelled":
      return "bg-red-100 text-red-800";
    default:
      return "bg-green-100 text-green-800";
  }
};

const Orders = ({ onSort = () => {} }) => {
  const {
    getCurrentDayOrders,
    changePaymentMethod,
    getOrderDetails,
    syncCloud,
    reports,
    settleOrder,
  } = useCloud();
  const { getAllCompletedOrders } = useOrder();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tablesVPAMapping] = useState(
    store.tables.reduce((acc, table) => {
      acc[table.qrpidVPA] = `${table.room} T${table.tableNo}`;
      return acc;
    }, {})
  );
  const [staffMapping] = useState(
    store.staff.reduce((acc, staff) => {
      acc[staff.id] = staff.name;
      return acc;
    }, {})
  );

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isViewBillOpen, setIsViewBillOpen] = useState(false);
  const [viewBillSelectedItem, setViewBillSelectedItem] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [error, setError] = useState("");
  const [isCancleOrder, setIsCancleOrder] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(1);

  // Get current page from URL params
  const currentPage = parseInt(searchParams.get("page")) || 1;

  // Function to update page in URL
  const updatePage = (newPage) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("page", newPage.toString());
    setSearchParams(newSearchParams);
  };

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [activeFilters, setActiveFilters] = useState({
    paymentMethods: [],
    statuses: [],
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef(null);

  // Pagination states
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [itemsPerPage] = useState(10); // Assuming 10 items per page

  // Handle opening the modal for cancellation
  const handleOpenCancelModal = (order) => {
    setSelectedOrder(order);
    setIsCancleOrder(true);
    setError("");
  };

  // Handle closing the modal for cancellation
  const handleCloseModal = () => {
    setIsCancleOrder(false);
    setPaymentMethod("");
    setError("");
  };

  // Handle form submission (Cancel order)
  const handleChangeMethod = async () => {
    if (!paymentMethod) {
      setError("Select Payment Method.");
      return;
    }
    try {
      const billSettlePayload = {
        orderId: selectedOrder["orderId"],
        tableVPA: selectedOrder["tableVPA"],
        paymentPending: false,
        billTxnLogs: {
          ...selectedOrder.billTxnLogs,
          amount: parseInt(selectedOrder.amount),
          paymentType: paymentMethod,
        },
        txnLogs: [
          {
            paymentMethodName: paymentMethod,
            amount: parseFloat(selectedOrder.amount),
          },
        ],
      };

      const { status, data } = await settleOrder(billSettlePayload);

      if (status === SUCCESS) {
        toast.success("Order settled successfully");
        setForceUpdate((prev) => prev + 1);
        handleCloseModal();
      } else {
        toast.error("Something went wrong");
      }
    } catch (error) {
      setError("An error occurred. Please try again.");
    }
  };

  // Available payment methods and statuses for filtering
  const availablePaymentMethods = ["cash", "online", "card", "pending"];
  const availableStatuses = ["Paid", "Pending", "Cancelled"];

  // Toggle status filter
  const toggleStatusFilter = (status) => {
    setActiveFilters((prev) => {
      const statuses = prev.statuses.includes(status)
        ? prev.statuses.filter((s) => s !== status)
        : [...prev.statuses, status];

      return { ...prev, statuses };
    });
  };

  // Toggle payment method filter
  const togglePaymentMethodFilter = (method) => {
    setActiveFilters((prev) => {
      const methods = prev.paymentMethods.includes(method)
        ? prev.paymentMethods.filter((m) => m !== method)
        : [...prev.paymentMethods, method];

      return { ...prev, paymentMethods: methods };
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm("");
    setActiveFilters({
      paymentMethods: [],
      statuses: [],
    });
  };

  // Handle bill view
  // Handle view bill
  const handleBillView = async (order) => {
    const { data, status } = await getOrderDetails(order.orderId);
    if (status === SUCCESS) {
      order = { ...order, ...data };
      setViewBillSelectedItem(order);
      setIsViewBillOpen(true);
    } else {
      toast.error("Something went wrong please try again ");
    }
  };
  // Handle printing
  const handleBillPrint = async (order) => {
    const { data, status } = await getOrderDetails(order.orderId);
    if (status === SUCCESS) {
      order = { ...order, ...data };
      let tableDetails = store.rooms.reduce(
        (acc, room) => {
          const table = room.tables.find(
            (table) => table.qrpidVPA == order.tableVPA
          );
          if (table) {
            return {
              roomName: room.roomName,
              tableNo: table.tableNo,
            };
          }
          return acc;
        },
        {
          roomName: "",
          tableNo: "",
        }
      );
      await Reprint({ ...order,billStatus: getOrderStatus(order.billStatus), tableDetails: tableDetails });
    } else {
      toast.error("Something went wrong please try again ");
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    const reports_data = await reports({
      dateType: "today",
      page: currentPage,
    });

    if (reports_data && reports_data.status === SUCCESS) {
      const {
        data: { orders, totalAmountInRange, totalAmountTillNow },
        pagination,
      } = reports_data;
      
      // Set pagination data
      if (pagination) {
        const { totalPages, totalItems } = pagination;
        setTotalPages(parseInt(totalPages));
        setTotalOrders(parseInt(totalItems));
      }
      
      setOrders(orders);
      setFilteredOrders(orders);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [currentPage, forceUpdate]);

  useEffect(() => {
    let result = orders;

    // Apply search term
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      result = result.filter(
        (order) =>
          order.billNo?.toString().includes(lowercasedSearch) ||
          tablesVPAMapping[order.tableVPA]
            ?.toLowerCase()
            .includes(lowercasedSearch) ||
          order.paymentmethod?.toLowerCase().includes(lowercasedSearch) ||
          order.billStatus?.toLowerCase().includes(lowercasedSearch) ||
          new Date(order.createdAt)
            .toLocaleString()
            .toLowerCase()
            .includes(lowercasedSearch)
      );
    }

    // Apply payment method filters
    if (activeFilters.paymentMethods.length > 0) {
      result = result.filter((order) =>
        activeFilters.paymentMethods.includes(
          order.paymentmethod?.toLowerCase()
        )
      );
    }

    // Apply status filters
    if (activeFilters.statuses.length > 0) {
      result = result.filter((order) =>
        activeFilters.statuses.includes(order.billStatus)
      );
    }

    setFilteredOrders(result);
  }, [searchTerm, activeFilters, orders]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const formateDate = (time) => {
    const orderTime = new Date(time);
    const day = String(orderTime.getDate()).padStart(2, "0");
    const month = String(orderTime.getMonth() + 1).padStart(2, "0"); // Months are zero-indexed
    const year = orderTime.getFullYear();
    const formattedDate = `${day}-${month}-${year}`;
    return formattedDate;
  };

  return (
    <div className="bg-gray-50 ">
      <div className="max-w-[1400px] h-[calc(100vh)] w-[100vw] md:w-[calc(100vw-120px)] mx-auto p-4 box-border bg-gray-50 overflow-x-hidden relative hide-scrollbar">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 h-full overflow-x-scroll hide-scrollbar relative ">
          <div className="p-4 border-b border-gray-200 rounded-t-lg bg-white flex-wrap gap-2 sticky top-0 flex flex-col gap-4 rounded-t-lg bg-white">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="font-semibold text-lg text-gray-900">
                  Today's Order
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {filteredOrders.length} Orders
                </p>
              </div>

              <div className="flex gap-2">
                {/* Refresh */}
                <button
                  onClick={fetchReports}
                  className="inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium transition-colors hover:bg-gray-50"
                >
                  <RefreshCcwDot className="h-4 w-4 mr-2 text-stroke-700 cursor-pointer hover:text-green-500" />
                  Refresh
                </button>
                {/* Filter */}
                <button
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                  className={`inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium transition-colors ${
                    isFilterOpen ||
                    activeFilters.paymentMethods.length > 0 ||
                    activeFilters.statuses.length > 0
                      ? "bg-blue-50 text-blue-700 border-blue-300"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                  {(activeFilters.paymentMethods.length > 0 ||
                    activeFilters.statuses.length > 0) && (
                    <span className="ml-1 bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded-full">
                      {activeFilters.paymentMethods.length +
                        activeFilters.statuses.length}
                    </span>
                  )}
                </button>

                {/* Filter count */}
                {(searchTerm ||
                  activeFilters.paymentMethods.length > 0 ||
                  activeFilters.statuses.length > 0) && (
                  <button
                    onClick={clearFilters}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Clear
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center">
              {/* Search bar */}
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by bill number, table, payment method..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>
            </div>

            {/* Filter panel */}
            {isFilterOpen && (
              <div
                ref={filterRef}
                className="bg-white space-y-2 p-4 rounded-md border border-gray-200 mt-2 absolute top-[60px] right-4 shadow-lg shadow-md-bottom z-10"
              >
                {/* Payment Method Filters */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    Payment Method
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {availablePaymentMethods.map((method) => (
                      <button
                        key={method}
                        onClick={() => togglePaymentMethodFilter(method)}
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${
                          activeFilters.paymentMethods.includes(method)
                            ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                      >
                        {method.charAt(0).toUpperCase() + method.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Order Status Filters */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    Order Status
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {availableStatuses.map((status) => (
                      <button
                        key={status}
                        onClick={() => toggleStatusFilter(status)}
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${
                          activeFilters.statuses.includes(status)
                            ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          {loading ? (
            <TableSkeleton rows={5} columns={10} hasHeader={true} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th
                      onClick={() => onSort("id")}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    >
                      Bill No
                    </th>
                    <th
                      onClick={() => onSort("table")}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    >
                      Table
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      My Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Discount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Grand Total
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tips
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th
                      onClick={() => onSort("time")}
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    >
                      Date/Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      View/Print
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredOrders

                    .sort((a, b) => {
                      const dateDiff =
                        new Date(b.createdAt) - new Date(a.createdAt);
                      if (dateDiff !== 0) return dateDiff;
                      return a.billNo - b.billNo;
                    })
                    .map((order) => (
                      <tr
                        key={order.billNo}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div className="flex gap-1 items-center">
                            <span>{order.billNo}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {
                            order.typeOfOrder === 'Takeaway' ?  'Parcel' : `${tablesVPAMapping[order.tableVPA]} `
                          }
                         
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <p className="font-medium capitalize">
                            {order.paymentmethod}
                          </p>
                          {order.paymentmethod === "partial" &&
                            order?.partialAmoutsplit && (
                              <div>
                                {order.partialAmoutsplit.map(
                                  (ele, idx) =>
                                    ele.amount > 0 && (
                                      <p
                                        key={idx}
                                        className="text-xs text-gray-600"
                                      >
                                        {ele.name}: {ele.amount}
                                      </p>
                                    )
                                )}
                              </div>
                            )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {Number.parseFloat(order.billTxnLogs.subTotal)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {Number.parseFloat(order.billTxnLogs.discount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {Number.parseFloat(order.billTxnLogs.total)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {order.billTxnLogs.tip || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap flex">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full  ${getOredrStatusColor(
                              order.billStatus
                            )}`}
                            onClick={() => {
                              if (order.billStatus === "settle_later") {
                                handleOpenCancelModal(order);
                              }
                            }}
                          >
                            {order.canceled
                              ? "Cancelled"
                              : getOrderStatus(order.billStatus)}
                          </span>

                          {order.orderType === "digital" && (
                            <span
                              className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 `}
                            >
                              Digital
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 space-x-2">
                          <span>
                            {new Date(order.createdAt).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })}
                          </span>
                          <span>{formateDate(order.createdAt)}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 cursor-pointer">
                          <FontAwesomeIcon
                            icon={faEye}
                            className="mr-3"
                            onClick={() => handleBillView(order)}
                          />
                          {!order.canceled && (
                            <FontAwesomeIcon
                              icon={faPrint}
                              className="mr-3"
                              onClick={() => handleBillPrint(order)}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
            <div className="flex items-center text-sm text-gray-700">
              <span>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, totalOrders)} of{" "}
                {totalOrders} results
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => updatePage(Math.max(currentPage - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <div className="flex space-x-1">
                {[...Array(Math.min(5, totalPages))].map((_, index) => {
                  let pageNumber;
                  if (totalPages <= 5) {
                    pageNumber = index + 1;
                  } else if (currentPage <= 3) {
                    pageNumber = index + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNumber = totalPages - 4 + index;
                  } else {
                    pageNumber = currentPage - 2 + index;
                  }

                  return (
                    <button
                      key={pageNumber}
                      onClick={() => updatePage(pageNumber)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        currentPage === pageNumber
                          ? "bg-blue-600 text-white"
                          : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() =>
                  updatePage(Math.min(currentPage + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Change Payment Method Modal */}
        {isCancleOrder && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Change Payment Method
              </h3>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md">
                  {error}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Method
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => {
                    setPaymentMethod(e.target.value);
                    setError("");
                  }}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">Select Payment Method</option>
                  <option value="cash">Cash</option>
                  <option value="online">Online</option>
                  <option value="card">Card</option>
                </select>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  onClick={handleChangeMethod}
                >
                  Change Method
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Bill Modal */}
        {isViewBillOpen && viewBillSelectedItem && (
          <BillDetailsModal
            viewBillSelectedItem={viewBillSelectedItem}
            setIsViewBillOpen={setIsViewBillOpen}
          />
        )}
      </div>
    </div>
  );
};

// Bill Details Modal Component

const BillDetailsModal = ({ viewBillSelectedItem, setIsViewBillOpen }) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto relative">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            Order Items (Bill No: {viewBillSelectedItem.billNo})
          </h2>
          {!viewBillSelectedItem.canceled && (
            <span
              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full  ${getOredrStatusColor(
                viewBillSelectedItem.billStatus
              )}`}
            >
              {getOrderStatus(viewBillSelectedItem.billStatus)}
            </span>
          )}
        </div>

        {viewBillSelectedItem.canceled && (
          <div className="mb-4 p-3 bg-red-50 rounded-md border border-red-100">
            <span className="inline-block px-3 py-1 text-sm font-medium rounded-full bg-red-200 text-red-800 mb-2">
              Cancelled
            </span>
            <p className="text-gray-700">
              Reason: {viewBillSelectedItem?.cancelReason || "Not specified"}
            </p>
          </div>
        )}

        {/* List of order items */}
        <div className="mb-6">
          <div className="grid grid-cols-12 gap-2 py-2 font-medium text-gray-700 border-b border-gray-200">
            <div className="col-span-5">Item</div>
            <div className="col-span-3">Variant</div>
            <div className="col-span-1 text-center">Qty</div>
            <div className="col-span-2 text-right">Price</div>
          </div>

          {viewBillSelectedItem?.items &&
            viewBillSelectedItem?.items.map((item, index) => (
              <div
                key={index}
                className="grid grid-cols-12 gap-2 py-3 border-b border-gray-100 items-center"
              >
                <div className="col-span-5 font-medium text-gray-800">
                  {item?.name || "Unknown Item"}{" "}
                  {item.isCancelled && (
                    <span className="block  py-0.5 text-xs font-medium rounded-full  text-red-800">
                      Cancelled
                    </span>
                  )}
                </div>
                <div className="col-span-3 text-sm text-gray-600">
                  {item.variantName || "—"}
                </div>
                <div className="col-span-1 text-center text-gray-600">
                  {item?.quantity || "1"}
                </div>
                <div className="col-span-2 text-right font-medium">
                  {Number.parseFloat(item?.price).toFixed(2)}
                </div>
              </div>
            ))}
        </div>

        {/* Summary */}
        <div className="border-t border-gray-200 pt-4 space-y-2">
          <div className="flex justify-between text-gray-700">
            <span>Sub Total:</span>
            <span>{viewBillSelectedItem?.billTxnLogs?.subTotal || 0}</span>
          </div>

          {viewBillSelectedItem?.billTxnLogs?.discount > 0 && (
            <div className="flex justify-between text-gray-700">
              <span>Discount:</span>
              <span>{viewBillSelectedItem?.billTxnLogs.discount}</span>
            </div>
          )}

          {viewBillSelectedItem?.billTxnLogs?.gst && (
            <>
              <div className="flex justify-between text-gray-700">
                <span>
                  CGST ({viewBillSelectedItem?.billTxnLogs?.cgstPercent || 0}%):
                </span>
                <span>{viewBillSelectedItem?.billTxnLogs?.cgst || 0}</span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span>
                  SGST ({viewBillSelectedItem?.billTxnLogs?.sgstPercent || 0}%):
                </span>
                <span>{viewBillSelectedItem?.billTxnLogs?.sgst || 0}</span>
              </div>
            </>
          )}

          {viewBillSelectedItem?.billTxnLogs?.tips > 0 && (
            <div className="flex justify-between text-gray-700">
              <span>Tips:</span>
              <span>{viewBillSelectedItem?.billTxnLogs.tips}</span>
            </div>
          )}

          <div className="flex justify-between font-bold text-lg">
            <span>Total Bill:</span>
            <span>{viewBillSelectedItem?.billTxnLogs.total}</span>
          </div>

          {viewBillSelectedItem?.paymentmethod && (
            <div className="flex justify-between text-sm text-gray-600 italic">
              <span>Payment Method:</span>
              <span className="capitalize">
                {viewBillSelectedItem?.paymentmethod}
              </span>
            </div>
          )}
        </div>

        {/* Complete Bill Information (Collapsible) */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <button
            className="flex justify-between items-center w-full text-left font-semibold text-gray-800 focus:outline-none"
            onClick={() => setIsDetailsOpen(!isDetailsOpen)}
          >
            <span>Complete Bill Information</span>
            {isDetailsOpen ? (
              <ChevronUp className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
          </button>

          {isDetailsOpen && (
            <div className="bg-gray-50 p-4 rounded-md text-sm mt-3">
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2 pb-2 mb-2 border-b border-gray-200">
                  <span className="font-medium">Bill Details</span>
                </div>

                <div className="text-gray-600">Sub Total:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.subTotal || 0}
                </div>

                <div className="text-gray-600">Before Discount Total:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.beforeDiscountTotal || 0}
                </div>

                <div className="text-gray-600">Total:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.total || 0}
                </div>

                <div className="col-span-2 pt-2 pb-2 mt-2 mb-2 border-t border-b border-gray-200">
                  <span className="font-medium">Discounts</span>
                </div>

                <div className="text-gray-600">Discount:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.discount || 0}
                </div>

                <div className="text-gray-600">Discount (Flat):</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.discountFlat || 0}
                </div>

                <div className="text-gray-600">Discount (%):</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.discountPercent || 0}%
                </div>

                <div className="col-span-2 pt-2 pb-2 mt-2 mb-2 border-t border-b border-gray-200">
                  <span className="font-medium">Taxes</span>
                </div>

                <div className="text-gray-600">GST Applied:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.gst ? "Yes" : "No"}
                </div>

                <div className="text-gray-600">GST Excluded:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.gstExcluded
                    ? "Yes"
                    : "No"}
                </div>

                <div className="text-gray-600">GST Number:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.gstNo || "N/A"}
                </div>

                <div className="text-gray-600">CGST (%):</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.cgstPercent || 0}%
                </div>

                <div className="text-gray-600">CGST Amount:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.cgst || 0}
                </div>

                <div className="text-gray-600">SGST (%):</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.sgstPercent || 0}%
                </div>

                <div className="text-gray-600">SGST Amount:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.sgst || 0}
                </div>

                <div className="col-span-2 pt-2 pb-2 mt-2 mb-2 border-t border-b border-gray-200">
                  <span className="font-medium">Other</span>
                </div>

                <div className="text-gray-600">Service Charge:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.service ? "Yes" : "No"}
                </div>

                <div className="text-gray-600">Tip:</div>
                <div className="text-right">
                  {viewBillSelectedItem?.billTxnLogs?.tip || 0}
                </div>

                <div className="text-gray-600">Payment Type:</div>
                <div className="text-right capitalize">
                  {viewBillSelectedItem?.billTxnLogs?.paymentType || "N/A"}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Close button */}
        <button
          className="mt-6 w-full bg-gray-700 hover:bg-gray-800 text-white py-3 px-4 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          onClick={() => setIsViewBillOpen(false)}
        >
          Close
        </button>
      </div>
    </div>
  );
};

// Table Skeleton Component
function TableSkeleton({ rows = 5, columns = 4, hasHeader = true }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        {hasHeader && (
          <thead>
            <tr className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              {Array(columns)
                .fill(null)
                .map((_, index) => (
                  <th key={`header-${index}`} className="px-4 py-3">
                    <div className="h-2.5 w-24 animate-pulse rounded-full bg-gray-200"></div>
                  </th>
                ))}
            </tr>
          </thead>
        )}
        <tbody className="divide-y divide-gray-100 bg-white">
          {Array(rows)
            .fill(null)
            .map((_, rowIndex) => (
              <tr key={`row-${rowIndex}`} className="text-gray-700">
                {Array(columns)
                  .fill(null)
                  .map((_, colIndex) => (
                    <td
                      key={`cell-${rowIndex}-${colIndex}`}
                      className="px-4 py-3"
                    >
                      <div
                        className={`h-2.5 animate-pulse rounded-full bg-gray-200 ${
                          colIndex === 0 ? "w-32" : "w-24"
                        }`}
                      ></div>
                    </td>
                  ))}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

export default Orders;
